import { isPlatformBrowser } from '@angular/common';
import { afterNextRender, DestroyRef, DOCUMENT, inject, Injector, PLATFORM_ID, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { Event as RouterEvent } from '@angular/router';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { pathOf } from '@app/core/shell/route-path.util';

const ROUTE_TITLE_SELECTOR = '.route-focus-target';
const MAIN_CONTENT_SELECTOR = '#main-content';

@Service()
export class MainContentFocusService {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);

  private isTheNavigationThatOpenedTheApp = !this.router.navigated;
  private lastPath: string | null = null;

  public init(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.router.events
      .pipe(
        filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(event => this.onNavigationEnd(event));
  }

  public focusMainContent(): void {
    const start = this.document.querySelector<HTMLElement>(ROUTE_TITLE_SELECTOR) ?? this.document.querySelector<HTMLElement>(MAIN_CONTENT_SELECTOR);

    if (start === null) {
      return;
    }

    start.focus({ preventScroll: true });
    this.keepTheFocusOnTheStart(start);
  }

  private keepTheFocusOnTheStart(start: HTMLElement): void {
    afterNextRender(
      () => {
        if (this.document.activeElement !== start) {
          start.focus({ preventScroll: true });
        }
      },
      { injector: this.injector },
    );
  }

  private onNavigationEnd(event: NavigationEnd): void {
    const path = pathOf(event.urlAfterRedirects);
    const isSameRoute = path === this.lastPath;
    this.lastPath = path;

    if (isSameRoute) {
      return;
    }

    if (this.isTheNavigationThatOpenedTheApp) {
      this.isTheNavigationThatOpenedTheApp = false;

      return;
    }

    afterNextRender(() => this.focusMainContent(), { injector: this.injector });
  }
}
